import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { analytics } from '../lib/analytics'

export type ConsentChoice = 'accepted' | 'rejected'

const STORAGE_KEY = 'gl_cookie_consent'

interface ConsentContextValue {
  /** null = user hasn't been asked yet (or hasn't answered) */
  choice: ConsentChoice | null
  accept: () => void
  reject: () => void
}

const ConsentContext = createContext<ConsentContextValue | null>(null)

function readStoredChoice(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'accepted' || raw === 'rejected' ? raw : null
  } catch {
    // localStorage unavailable (private mode, etc.) — treat as unanswered each visit
    return null
  }
}

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [choice, setChoice] = useState<ConsentChoice | null>(readStoredChoice)

  // React to the stored choice — this runs on mount too, so a returning
  // user who already accepted gets analytics initialized without seeing
  // the banner again.
  useEffect(() => {
    if (choice === 'accepted') {
      analytics.init()
    } else {
      analytics.shutdown()
    }
  }, [choice])

  const persist = (next: ConsentChoice) => {
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Best effort — if storage is unavailable the banner will just
      // reappear next visit, which is an acceptable degradation.
    }
    setChoice(next)
  }

  return (
    <ConsentContext.Provider
      value={{
        choice,
        accept: () => persist('accepted'),
        reject: () => persist('rejected'),
      }}
    >
      {children}
    </ConsentContext.Provider>
  )
}

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext)
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider')
  return ctx
}
